<template>
  <div v-if="ready">
    <rui-navbar :caption="map.name">
      <template #left>
        <rui-button icon="notebook" text @click="showingObservations = map">
          {{ $t('js.layouts.shared_maps.map_observations') }} <small v-if="map.observationsCount > 0">({{ map.observationsCount }})</small>
        </rui-button>
      </template>

      <template #right>
        <gp-theme-switcher-menu-item />
      </template>
    </rui-navbar>

    <rui-tabs no-text-on-small>
      <rui-button :to="{ name: 'map', params: $route.params }" icon="leaf">
        {{ $t('js.layouts.garden.toolbar.map') }}
      </rui-button>

      <rui-button :to="{ name: 'observations', params: $route.params}" icon="observations">
        {{ $t('js.layouts.garden.toolbar.timeline') }}
      </rui-button>
    </rui-tabs>

    <router-view />

    <rui-modal v-if="showingObservations"
               :caption="$t('js.layouts.shared_maps.map_observations')"
               :cancellable="false"
               @accept="showingObservations = null"
               @close="showingObservations = null">
      <gpa-observations-list :subject="showingObservations" />
    </rui-modal>

    <!-- Other widgets -->
    <rui-image-viewer v-if="imageToDisplay"
                      :list="imagesList"
                      :preselected="imageToDisplay"
                      @close="imageToDisplay = null" />

    <rui-confirm-dialog v-if="confirmDialogOptions"
                        :caption="confirmDialogOptions.caption"
                        :message="confirmDialogOptions.message"
                        @accept="acceptConfirmDialog"
                        @reject="confirmDialogOptions = null">
      <component :is="confirmDialogOptions.component" v-if="confirmDialogOptions?.component" />
    </rui-confirm-dialog>
  </div>
  <rui-loading v-else :text="$t('generic.loading')" fill />
</template>

<script>
import RuiButton from '@experiments-labs/rise_ui/src/components/Button/Button.vue'
import RuiImageViewer from '@experiments-labs/rise_ui/src/components/DataDisplay/ImageViewer/ImageViewer.vue'
import RuiLoading from '@experiments-labs/rise_ui/src/components/Loading/Loading.vue'
import RuiConfirmDialog from '@experiments-labs/rise_ui/src/components/Modal/ConfirmDialog.vue'
import RuiModal from '@experiments-labs/rise_ui/src/components/Modal/Modal.vue'
import RuiNavbar from '@experiments-labs/rise_ui/src/components/Navbar/Navbar.vue'
import RuiTabs from '@experiments-labs/rise_ui/src/components/Tabs/Tabs.vue'

import GpThemeSwitcherMenuItem from '../../common/ThemeSwitcherMenuItem.vue'

import GpaObservationsList from '../garden/components/Observations/_Observations.vue'

/**
 * @typedef {import('../../classes/models/Patch')}    Patch
 * @typedef {import('../../classes/models/Path')}     Path
 * @typedef {import('../../classes/models/Layer')}    Layer
 * @typedef {import('../../classes/models/Genus')}    Genus
 * @typedef {import('../../classes/models/Resource')} Resource
 * @typedef {import('../../classes/models/Layer')}    Layer
 */

export default {
  name: 'SharedGardenApplication',
  components: { RuiConfirmDialog, GpThemeSwitcherMenuItem, RuiLoading, RuiImageViewer, RuiModal, RuiTabs, RuiButton, RuiNavbar, GpaObservationsList },
  data () {
    return {
      /** @type {boolean} True when all api calls succeeds */
      ready: false,

      showingObservations: null,

      // Image viewer
      imagesList: [],
      imageToDisplay: null,

      // Confirm dialog
      confirmDialogOptions: null,
    }
  },
  computed: {
    map () { return this.$store.getters.currentMap },
  },
  methods: {
    acceptConfirmDialog () {
      this.confirmDialogOptions.callback()
      this.confirmDialogOptions = null
    },
  },
  created () {
    this.$onBus('show-image',
      /** @param {{selected: string, list: string[]}} images - Widget configuration */
      (images) => {
        this.imagesList = images.list
        this.imageToDisplay = images.selected
      })

    this.$onBus('show-observations', (subject) => {
      this.showingObservations = subject
    })

    this.$onBus('ask-confirmation',
      /** @param { {caption: string, message: string, component: ?object, callback: Function} } dialogOptions - Options to create the modal */
      (dialogOptions) => { this.confirmDialogOptions = dialogOptions })

    Promise.all([
      this.$store.dispatch('loadAccount', false),
      this.$store.dispatch('loadSharedGarden', this.$mapId),
    ])
      .then(() => {
        this.ready = true
        this.$store.dispatch('setCurrentMap', this.$mapId)
      })
  },
}
</script>

<style lang="scss" src="./App.scss" />
